import type {
  AnnotationComment,
  Attachment,
  ChannelId,
  CommentId,
  MessageId,
  MockCache,
  TaskId,
  UserId,
} from '../../types';
import type { InboxWish } from '../inbox-delivery';
import { channelLabel, handleMentionedUserIds } from '../internal';

const PREVIEW_LENGTH = 120;

type Place = Pick<InboxWish, 'kind' | 'sourceKind' | 'sourceId' | 'taskId' | 'channelId'> & {
  where: string;
  sees: (userId: UserId) => boolean;
};

function seesChannel(cache: MockCache, channelId: ChannelId, userId: UserId): boolean {
  const channel = cache.channels.get(channelId);
  if (channel === undefined) return false;
  return (channel.kind === 'public' && !channel.closed) || cache.members.has(`${String(channelId)}:${String(userId)}`);
}

function messagePlace(cache: MockCache, messageId: MessageId): Place | null {
  const message = cache.messages.get(messageId);
  const channel = message === undefined ? undefined : cache.channels.get(message.channelId);
  if (message === undefined || channel === undefined || message.deletedAt !== null) return null;
  return {
    kind: 'chat_mention',
    sourceKind: 'message',
    sourceId: String(message.id),
    taskId: null,
    channelId: message.channelId,
    where: channelLabel(cache, channel),
    sees: (userId) => seesChannel(cache, message.channelId, userId),
  };
}

function taskPlace(cache: MockCache, taskId: TaskId, source: Pick<Place, 'sourceKind' | 'sourceId'>): Place | null {
  const task = cache.tasks.get(taskId);
  if (task === undefined || task.deletedAt !== null) return null;
  return { kind: 'mention', ...source, taskId: task.id, channelId: null, where: task.key, sees: () => true };
}

function commentPlace(cache: MockCache, commentId: CommentId): Place | null {
  const comment = cache.comments.get(commentId);
  if (comment === undefined || comment.deletedAt !== null) return null;
  return taskPlace(cache, comment.taskId, { sourceKind: 'comment', sourceId: String(comment.id) });
}

function placeOf(cache: MockCache, attachment: Attachment): Place | null {
  switch (attachment.ownerKind) {
    case 'message':
      return messagePlace(cache, attachment.ownerId as MessageId);
    case 'comment':
      return commentPlace(cache, attachment.ownerId as CommentId);
    case 'doc':
      return null;
    default:
      return taskPlace(cache, attachment.ownerId as TaskId, {
        sourceKind: 'task',
        sourceId: String(attachment.ownerId),
      });
  }
}

export function annotationMentionWishes(
  cache: MockCache,
  attachment: Attachment,
  comment: AnnotationComment,
): InboxWish[] {
  const place = placeOf(cache, attachment);
  if (place === null) return [];
  const preview = `Вас упомянули в комментарии к файлу «${attachment.name}» · ${place.where}: ${comment.bodyText.slice(0, PREVIEW_LENGTH)}`;
  return handleMentionedUserIds(cache, comment.bodyText)
    .filter((userId) => userId !== cache.me && place.sees(userId))
    .map((userId) => ({
      userId,
      kind: place.kind,
      sourceKind: place.sourceKind,
      sourceId: place.sourceId,
      taskId: place.taskId,
      channelId: place.channelId,
      preview,
    }));
}
